import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { JourneyScene } from './JourneyScene';

const STAGES = [
  { n: '01', title: 'Packed at origin', text: 'Cargo is palletised and stuffed into a Südvik container at the shipper\'s warehouse. Seal number and verified gross mass are captured digitally.' },
  { n: '02', title: 'Lifted onto the chassis', text: 'A reach stacker lifts the sealed box onto the truck. The gate-out event posts to your tracking timeline within minutes.' },
  { n: '03', title: 'Inland haulage', text: 'Carrier haulage moves the container to the terminal by road or rail against a pre-advised appointment.' },
  { n: '04', title: 'Terminal lift', text: 'A ship-to-shore gantry lifts the container from the chassis and stows it according to the bay plan.' },
  { n: '05', title: 'Ocean voyage', text: 'The vessel departs on the published date. Waypoint ETAs and milestone emails keep you informed until delivery.' },
];

export function ContainerJourney() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.4 });
  const railWidth = useTransform(progress, [0, 1], ['0%', '100%']);

  return (
    <section ref={ref} className="relative bg-gray-50 border-y border-gray-200" style={{ height: '520vh' }} data-testid="container-journey">
      <div className="sticky top-0 h-screen overflow-hidden">
        <div className="absolute top-0 inset-x-0 z-20 pt-24 md:pt-28">
          <div className="sv-wrap flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <p className="sv-kicker">The journey of a container</p>
              <h2 className="text-[32px] md:text-[40px] sv-display mt-2">From warehouse floor to open water.</h2>
            </div>
            <p className="text-gray-600 text-sm md:text-base max-w-sm">Keep scrolling. Every stage of a shipment, exactly as it happens on the ground.</p>
          </div>
        </div>

        <div className="absolute inset-0 flex items-center justify-center">
          <JourneyScene progress={progress} />
        </div>
        <div className="absolute inset-x-0 top-0 h-64 bg-gradient-to-b from-gray-50 via-gray-50/80 to-transparent pointer-events-none z-10" />
        <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-gray-50 via-gray-50/90 to-transparent pointer-events-none z-10" />

        <div className="absolute bottom-0 inset-x-0 z-20 pb-8 md:pb-12">
          <div className="sv-wrap">
            <div className="relative h-32 md:h-24">
              {STAGES.map(function (s, i) { return <Caption key={s.n} stage={s} index={i} progress={progress} />; })}
            </div>
            <div className="mt-6 flex items-center gap-4">
              <div className="flex-1 h-[3px] bg-gray-200 relative overflow-hidden rounded">
                <motion.div className="absolute inset-y-0 left-0 bg-ink" style={{ width: railWidth }} />
              </div>
              <div className="hidden md:flex gap-6 text-[12px] sv-mono text-gray-600">
                {STAGES.map(function (s) { return <span key={s.n}>{s.n}</span>; })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Caption({ stage, index, progress }) {
  const start = index * 0.2;
  const end = index === 4 ? 1.01 : start + 0.19;
  const opacity = useTransform(progress, [start, start + 0.03, start + 0.16, end], [index === 0 ? 1 : 0, 1, 1, index === 4 ? 1 : 0]);
  const y = useTransform(progress, [start, start + 0.03], [index === 0 ? 0 : 10, 0]);
  return (
    <motion.div style={{ opacity, y }} className="absolute inset-x-0 top-0 grid md:grid-cols-12 gap-4 items-start" data-testid={"journey-stage-" + stage.n}>
      <div className="md:col-span-1 sv-mono text-[15px] text-gray-600 pt-2">{stage.n}</div>
      <h3 className="md:col-span-4 text-2xl md:text-[28px] sv-display">{stage.title}</h3>
      <p className="md:col-span-6 text-gray-700 text-sm md:text-base">{stage.text}</p>
    </motion.div>
  );
}
